import { Capacitor } from "@capacitor/core";
import {
  INTRO_ELIGIBILITY_STATUS,
  LOG_LEVEL,
  Purchases,
  type CustomerInfo,
  type PurchasesPackage,
} from "@revenuecat/purchases-capacitor";
import { z } from "zod";

export const revenueCatEntitlement = import.meta.env.VITE_REVENUECAT_ENTITLEMENT ?? "samklang_access";

const supportedProductIds = ["samklang_solo_monthly"] as const;

const ProductIdSchema = z.enum(supportedProductIds);
export type BillingProductId = z.infer<typeof ProductIdSchema>;

export function normalizeStoreProductId(storeIdentifier: string): BillingProductId | null {
  const productIdentifier = storeIdentifier.split(":", 1)[0];
  const parsed = ProductIdSchema.safeParse(productIdentifier);
  return parsed.success ? parsed.data : null;
}

const CustomerAccessSchema = z.object({
  entitlements: z.object({
    active: z.record(z.string(), z.unknown()),
  }),
  managementURL: z.string().url().nullable(),
});

const PackageDisplaySchema = z.object({
  identifier: z.string().min(1),
  packageType: z.string().min(1),
  product: z.object({
    identifier: z.string().min(1),
    title: z.string().min(1),
    description: z.string(),
    priceString: z.string().min(1),
  }),
});

export interface BillingPackage {
  readonly id: string;
  readonly productId: BillingProductId;
  readonly title: string;
  readonly description: string;
  readonly price: string;
  readonly period: "monthly";
  readonly plan: "SOLO";
  readonly trialEligibility: "eligible" | "ineligible" | "unknown";
  readonly nativePackage: PurchasesPackage;
}

export interface StoreAccess {
  readonly active: boolean;
  readonly managementUrl: string | null;
}

export function trialEligibilityFromRevenueCatStatus(
  status: INTRO_ELIGIBILITY_STATUS | undefined,
): BillingPackage["trialEligibility"] {
  if (status === INTRO_ELIGIBILITY_STATUS.INTRO_ELIGIBILITY_STATUS_ELIGIBLE) return "eligible";
  if (
    status === INTRO_ELIGIBILITY_STATUS.INTRO_ELIGIBILITY_STATUS_INELIGIBLE
    || status === INTRO_ELIGIBILITY_STATUS.INTRO_ELIGIBILITY_STATUS_NO_INTRO_OFFER_EXISTS
  ) return "ineligible";
  return "unknown";
}

let configured = false;
let configuredOrganizationId: string | null = null;

function platformApiKey(): string | null {
  const platform = Capacitor.getPlatform();
  if (platform === "ios") return import.meta.env.VITE_REVENUECAT_IOS_API_KEY ?? null;
  if (platform === "android") return import.meta.env.VITE_REVENUECAT_ANDROID_API_KEY ?? null;
  return null;
}

export function isNativeBillingAvailable(): boolean {
  return Capacitor.isNativePlatform() && platformApiKey() !== null;
}

export async function initializeBilling(organizationId: string): Promise<void> {
  const parsedOrganizationId = z.string().uuid().parse(organizationId);
  if (!Capacitor.isNativePlatform()) return;
  const apiKey = platformApiKey();
  if (!apiKey) throw new Error("BILLING_NOT_CONFIGURED");
  if (!configured) {
    if (import.meta.env.DEV) await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG });
    await Purchases.configure({ apiKey, appUserID: parsedOrganizationId });
    configured = true;
    configuredOrganizationId = parsedOrganizationId;
    return;
  }
  if (configuredOrganizationId !== parsedOrganizationId) {
    await Purchases.logIn({ appUserID: parsedOrganizationId });
    configuredOrganizationId = parsedOrganizationId;
  }
}

function parseStoreAccess(customerInfo: CustomerInfo): StoreAccess {
  const parsed = CustomerAccessSchema.parse(customerInfo);
  return {
    active: Object.hasOwn(parsed.entitlements.active, revenueCatEntitlement),
    managementUrl: parsed.managementURL,
  };
}

export async function getStoreAccess(): Promise<StoreAccess> {
  if (!configured) return { active: false, managementUrl: null };
  const { customerInfo } = await Purchases.getCustomerInfo();
  return parseStoreAccess(customerInfo);
}

export async function getBillingPackages(): Promise<readonly BillingPackage[]> {
  if (!configured) return [];
  const offerings = await Purchases.getOfferings();
  const packages = offerings.current?.availablePackages ?? [];
  const supportedPackages = packages.flatMap((nativePackage) => {
    const parsed = PackageDisplaySchema.safeParse(nativePackage);
    if (!parsed.success) return [];
    const productId = normalizeStoreProductId(parsed.data.product.identifier);
    if (!productId) return [];
    return [{ nativePackage, parsed: parsed.data, productId }];
  });
  let eligibilityByProduct: Awaited<ReturnType<typeof Purchases.checkTrialOrIntroductoryPriceEligibility>> = {};
  if (Capacitor.getPlatform() === "ios") {
    try {
      eligibilityByProduct = await Purchases.checkTrialOrIntroductoryPriceEligibility({
        productIdentifiers: supportedPackages.map(({ parsed }) => parsed.product.identifier),
      });
    } catch {
      eligibilityByProduct = {};
    }
  }
  return supportedPackages.map(({ nativePackage, parsed, productId }) => {
    const trialEligibility = trialEligibilityFromRevenueCatStatus(eligibilityByProduct[parsed.product.identifier]?.status);
    return {
      id: parsed.identifier,
      productId,
      title: parsed.product.title,
      description: parsed.product.description,
      price: parsed.product.priceString,
      plan: "SOLO",
      period: "monthly",
      trialEligibility,
      nativePackage,
    } satisfies BillingPackage;
  });
}

export async function purchaseBillingPackage(selectedPackage: BillingPackage): Promise<StoreAccess> {
  if (!configured) throw new Error("BILLING_NOT_CONFIGURED");
  const validatedProductId = normalizeStoreProductId(selectedPackage.nativePackage.product.identifier);
  if (validatedProductId !== selectedPackage.productId) throw new Error("BILLING_PRODUCT_MISMATCH");
  const result = await Purchases.purchasePackage({ aPackage: selectedPackage.nativePackage });
  return parseStoreAccess(result.customerInfo);
}

export async function restoreBillingPurchases(): Promise<StoreAccess> {
  if (!configured) throw new Error("BILLING_NOT_CONFIGURED");
  const { customerInfo } = await Purchases.restorePurchases();
  return parseStoreAccess(customerInfo);
}
